import React from "react"
import Layout from "../../components/layout"
import Page from "../../components/page"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import PolicyPapers from "../../components/policy-papers"
import ReadNext from "../../components/read-next"
import { StatContainer } from "../../components/stat"
import IconEyeglassClipboard from "../../images/what-we-do/economic-surveillance/eyeglass-clipboard.inline.svg"

export default function EconomicSurveillance() {
  return (
    <Layout>
      <Hero
        title="Economic Surveillance"
        image="what-we-do/economic-surveillance/hero.jpg"
        basic
      />
      <Page>
        <PageColumns>
          <Column location="left">
            <StatContainer classes="stat-container--hero">
              <header>
                <h2>
                  126
                </h2>
                <p className="stat__subhead">
                  country health checks
                </p>
                <div className="stat__icon">
                  <IconEyeglassClipboard />
                </div>
              </header>
              <p>
                The IMF monitors the international monetary system as well as
                the economic and financial policies of its member countries. As
                part of this monitoring, known as{" "}
                <a href="https://www.imf.org/en/About/Factsheets/IMF-Surveillance">
                  surveillance
                </a>
                , which takes place both at the global level and in individual
                countries, the IMF highlights possible risks to stability and
                advises on policy adjustments to address these risks. Country
                surveillance culminates in regular (usually annual)
                consultations with individual member countries, known as Article
                IV consultations. For members with systemically important
                financial sectors, the IMF also conducts regular in-depth
                analysis of the countries’ financial sectors under the FSAP.
              </p>
            </StatContainer>
          </Column>

          <Column classes="text-block">
            <p className="dropcap">
              Through “surveillance,” the IMF monitors the international
              monetary system and global economic developments and engages in
              annual health checks of the economic and financial policies of its
              190 member countries. In addition, the IMF highlights possible
              stability risks to its member countries and advises their
              governments on potential policy adjustments, enabling the
              international monetary system to achieve its goal of facilitating
              the exchange of goods, services, and capital among countries,
              thereby sustaining sound economic growth. The IMF gives
              country-specific advice through “bilateral surveillance” and
              provides analysis of the international monetary system and global
              and regional economic developments through “multilateral
              surveillance.” In practice, these two aspects of surveillance
              inform each other, ensuring a more comprehensive, consistent
              analysis of “spillovers”: how one country’s policies affect other
              countries.
            </p>
            <p>
              In this era of unprecedented economic uncertainty, timely and
              tailored surveillance remains vital to share lessons and provide
              members with well-calibrated policy advice.
            </p>
            <h2>Bilateral surveillance</h2>
            <p>
              Article IV consultations consist of a two-way policy dialogue
              between the IMF and country authorities and cover a range of
              important issues: fiscal, financial, foreign exchange, monetary,
              and structural. In FY 2022, the IMF conducted 126 Article IV
              consultations and 11 financial system stability assessments under
              the FSAP.
            </p>
            <p>
              Following the recent{" "}
              <a href="https://www.imf.org/en/Topics/Comprehensive-Surveillance-Review">
                Comprehensive Surveillance Review
              </a>{" "}
              and the{" "}
              <a href="https://www.imf.org/en/Publications/Policy-Papers/Issues/2021/06/01/2021-Financial-Sector-Assessment-Program-Review-Background-Paper-On-Scope-460585">
                review of the FSAP
              </a>
              , the IMF is continuing to refine its bilateral surveillance
              activities, including by strengthening systemic risk analysis,
              better anchoring macroprudential policy advice, improving the
              Fund’s forecasting and monitoring frameworks, and furthering the
              work on data provision and standards. Guidance on medium-term
              fiscal frameworks is also being strengthened.
            </p>
            <h2>Multilateral surveillance</h2>
            <p>
              As part of its multilateral surveillance, the IMF issues biannual
              reports and updates on the latest global economic developments:
              the{" "}
              <i>
                <a href="https://www.imf.org/en/Publications/WEO">
                  World Economic Outlook
                </a>
              </i>
              , the{" "}
              <i>
                <a href="https://www.imf.org/en/Publications/GFSR">
                  Global Financial Stability Report
                </a>
              </i>
              , and the{" "}
              <i>
                <a href="https://www.imf.org/en/Publications/FM">
                  Fiscal Monitor
                </a>
              </i>
              . In-depth analyses of selected and highly topical issues are
              included in these publications as thematic chapters. Interim
              updates on global economic conditions are issued when warranted.
              In addition, as part of an ongoing effort to provide a rigorous
              and candid assessment of global excess imbalances and their
              causes, the{" "}
              <i>
                <a href="https://www.imf.org/en/Publications/SPROLLs/External-Sector-Reports">
                  External Sector Report
                </a>
              </i>{" "}
              is published annually.
            </p>
            <p>
              Reports on the outlooks for regions are also published. These
              cover regional policy developments and challenges and provide
              country-specific data and analysis.
            </p>
            <p>
              Article IV consultations and financial system stability
              assessments under the FSAP also discuss issues related to
              multilateral surveillance, where relevant.
            </p>
            <h2>Institutional view on capital flows</h2>
            <p>
              The Institutional View on the Liberalization and Management of{" "}
              <a href="https://www.imf.org/en/Topics/Capital-Flows">
                Capital Flows
              </a>{" "}
              was updated in FY 2022 to consider appropriate preemptive use of
              capital flow management measures and macroprudential measures,
              even when there is no surge in capital inflows, to address certain
              financial stability risks. This change builds on the{" "}
              <a href="https://www.imf.org/en/Topics/IPF-Integrated-Policy-Framework">
                Integrated Policy Framework
              </a>
              , an IMF research effort to build a systematic framework to
              analyze policy options and trade-offs in response to shocks, given
              country-specific characteristics.
            </p>
            <p>
              Another relevant update gives special treatment to some categories
              of capital flow management measures. These include certain
              macroprudential measures imposed in line with the Basel Framework,
              tax measures based on certain international cooperation standards
              to prevent or diminish the avoidance or evasion of taxes, measures
              implemented in line with the international standard to combat
              money laundering and financing of terrorism, and measures
              introduced for national or international security reasons.
            </p>
            <p>
              Like other IMF policies, the Institutional View will continue to
              be informed by advances in research and experiences with its
              implementation, as well as by developments in the global economy.
            </p>
            <h2>Policy advice</h2>
            <p>
              The IMF Executive Board discusses all aspects of the IMF’s work,
              from Article IV consultations to policy issues relevant to the
              global economy. The Board carries out its work largely on the
              basis of policy papers prepared by the IMF staff. In FY 2022, the
              IMF published 72 of these policy papers externally. Below is a
              comprehensive list of IMF policy papers issued in FY 2022.
            </p>
          </Column>
        </PageColumns>
      </Page>

      <PolicyPapers />

      <ReadNext sectionID="what-we-do" pageID="lending" />
    </Layout>
  )
}
